import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ChatEvent,
  SuperReactionEvent,
  GameStateView,
  OverlayNote,
  ReactionEvent,
  ServerToClientEvents,
} from '@srcbattle/shared';
import { api, ApiRequestError } from '../api.ts';
import { useSession } from '../session.tsx';
import { useClock } from '../useClock.ts';
import { Avatar, Banner, Button, Card, Spinner } from '../components/ui.tsx';
import { MatchStage } from '../components/MatchStage.tsx';
import { VotePanel } from '../components/VotePanel.tsx';
import { ChatComposer, ChatOverlay, ReactionBar, ReactionOverlay, SuperReactionComposer, SuperReactionOverlay } from '../components/Reactions.tsx';
import { OverlayComposer, OverlayNotes } from '../components/OverlayNotes.tsx';
import { useSiteAssets } from '../siteAssets.ts';

type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

function BracketView({
  state,
  revealFuture = true,
}: {
  state: GameStateView;
  revealFuture?: boolean;
}) {
  const byRound = new Map<number, GameStateView['bracket']>();
  for (const node of state.bracket) {
    if (node.isBye) continue;
    const list = byRound.get(node.round) ?? [];
    list.push(node);
    byRound.set(node.round, list);
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {[...byRound.entries()].map(([round, nodes]) => (
        <div key={round} className="flex min-w-[150px] flex-col gap-2">
          <p className="text-xs font-medium text-text-faint">Round {round}</p>
          {nodes.map((node) => (
            <div
              key={node.id}
              className={`rounded-lg border px-2 py-1.5 text-xs ${
                node.isCurrent
                  ? 'border-accent bg-accent/10'
                  : node.winnerEntryId
                    ? 'border-border bg-surface-raised text-text-faint'
                    : 'border-dashed border-border text-text-faint'
              }`}
            >
              {/* Titles only. Who submitted them is not in this payload at all. */}
              {!revealFuture && !node.isCurrent && !node.winnerEntryId ? (
                <p className="py-1 text-center italic">Hidden until this match</p>
              ) : (
                <>
                  <p className="truncate">{node.aTitle ?? '—'}</p>
                  <p className="truncate">{node.bTitle ?? '—'}</p>
                </>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ChampionScreen({
  state,
  busy,
  onPlayAgain,
}: {
  state: GameStateView;
  busy: boolean;
  onPlayAgain: () => void;
}) {
  const champion = state.champion;
  const assets = useSiteAssets();
  if (!champion) return null;

  return (
    <div className="flex flex-col gap-4">
      <Card className="text-center">
        {assets.banner && (
          <img
            src={assets.banner}
            alt=""
            className="mx-auto mb-4 max-h-32 w-full rounded-lg object-cover"
          />
        )}
        <p className="text-sm text-text-muted">Champion</p>
        <h2 className="mt-1 text-2xl font-semibold tracking-tight">{champion.entry.title}</h2>

        <div className="mx-auto mt-4 w-full max-w-2xl overflow-hidden rounded-xl">
          {champion.entry.source === 'spotify' && champion.entry.spotifyTrackId ? (
            <iframe
              title={`Play ${champion.entry.title} on Spotify`}
              src={`https://open.spotify.com/embed/track/${champion.entry.spotifyTrackId}`}
              className="h-[152px] w-full border-0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            />
          ) : champion.entry.youtubeVideoId ? (
            <iframe
              title={`Play ${champion.entry.title} on YouTube`}
              src={`https://www.youtube.com/embed/${champion.entry.youtubeVideoId}?rel=0&playsinline=1&origin=${encodeURIComponent(window.location.origin)}`}
              className="aspect-video w-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          ) : null}
        </div>
        {champion.entry.youtubeVideoId && <a className="mt-2 inline-block text-xs text-text-muted underline hover:text-text" href={`https://www.youtube.com/watch?v=${champion.entry.youtubeVideoId}`} target="_blank" rel="noreferrer">Open the winner on YouTube if the embed is unavailable</a>}

        {champion.entry.submitter && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <Avatar value={champion.entry.submitter.avatarValue} size="lg" />
            <div className="text-left">
              <p className="font-medium">{champion.entry.submitter.displayName}</p>
              <p className="text-sm text-text-faint">wins the night</p>
            </div>
          </div>
        )}

        {champion.entry.greatMindsWith && champion.entry.greatMindsWith.length > 1 && (
          <p className="mt-3 rounded-lg bg-accent/15 px-3 py-2 text-sm text-accent">
            <strong>GREAT MINDS</strong> — {champion.entry.greatMindsWith.join(' and ')} both
            picked the winner.
          </p>
        )}
      </Card>

      <Card>
        <h3 className="mb-2 font-medium">How it got there</h3>
        <ol className="flex flex-col gap-1.5 text-sm">
          {champion.path.map((step) => (
            <li key={step.round} className="flex gap-2">
              <span className="w-24 shrink-0 text-text-faint">{step.roundName}</span>
              <span className="min-w-0 truncate">beat {step.beatTitle ?? 'a bye'}</span>
            </li>
          ))}
        </ol>
      </Card>
      {state.guessLeaderboard.length > 0 && <Card><h3 className="mb-2 font-medium">Best detective</h3>{state.guessLeaderboard.map((person, index) => <p key={person.userId} className="text-sm">{index + 1}. {person.displayName} — {person.correct} correct</p>)}</Card>}

      <Card>
        <h3 className="mb-2 font-medium">Full bracket</h3>
        <BracketView state={state} />
      </Card>

      {state.amHost && (
        <Card className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Go again?</p>
            <p className="text-xs text-text-faint">Clears submissions and draws a new bracket.</p>
          </div>
          <Button onClick={onPlayAgain} loading={busy}>
            Play again
          </Button>
        </Card>
      )}
    </div>
  );
}

export function Game({ lobbyId, onBack }: { lobbyId: string; onBack: () => void }) {
  const { session } = useSession();
  const clock = useClock(true);
  const [state, setState] = useState<GameStateView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // null means "whatever the lobby setting says"; a click pins it for this
  // session without changing the setting for everyone else.
  const [bracketOverride, setBracketOverride] = useState<boolean | null>(null);
  const [notes, setNotes] = useState<OverlayNote[]>([]);
  const [reaction, setReaction] = useState<ReactionEvent | null>(null);
  const [chat, setChat] = useState<ChatEvent | null>(null);
  const [superReaction, setSuperReaction] = useState<SuperReactionEvent | null>(null);
  const [showOverlayTools, setShowOverlayTools] = useState(false);
  const socketRef = useRef<AppSocket | null>(null);
  // Held in a ref so a new onBack identity does not tear down the socket.
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  const refresh = useCallback(async () => {
    try {
      setState(await api.getGame(lobbyId));
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.code === 'not_playing') {
        setState(null);
      } else {
        setError(caught instanceof ApiRequestError ? caught.message : 'Could not load the game.');
      }
    }
  }, [lobbyId]);

  useEffect(() => {
    void refresh();

    const socket: AppSocket = io({ withCredentials: true });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('lobby:subscribe', { lobbyId }, () => undefined);
      // Anything that changed while the socket was down was never delivered.
      void refresh();
    });
    socket.on('game:update', (next) => setState(next as GameStateView));

    // A reset wipes the game out from under everyone still looking at the
    // champion screen. There is no game state left to push, so the lobby update
    // is what carries the news — without this they sit on last game's result
    // until they happen to navigate away.
    socket.on('lobby:update', (update) => {
      const { status } = update;
      if (status && status !== 'playing' && status !== 'finished') onBackRef.current();
    });

    socket.on('overlay:update', (payload) => setNotes(payload.notes));
    // One slot rather than a queue: the overlay component keeps its own list of
    // what is still floating, and this is only the doorbell.
    socket.on('reaction', (event) => setReaction(event));
    socket.on('chat', (event) => setChat(event));
    socket.on('super-reaction', (event) => setSuperReaction(event));

    void api
      .overlays(lobbyId)
      .then(({ notes: current }) => setNotes(current))
      .catch(() => undefined);

    return () => {
      socket.emit('lobby:unsubscribe', { lobbyId });
      socket.close();
      socketRef.current = null;
    };
  }, [lobbyId, refresh]);

  useEffect(() => {
    if (!superReaction) return;
    const timer = window.setTimeout(() => setSuperReaction(null), state?.superReactionDurationMs ?? 3_000);
    return () => window.clearTimeout(timer);
  }, [superReaction, state?.superReactionDurationMs]);

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  if (error && !state) {
    return (
      <main className="mx-auto w-full max-w-lg px-4 py-12">
        <Banner tone="danger">{error}</Banner>
        <div className="mt-4">
          <Button onClick={onBack}>Back to the lobby</Button>
        </div>
      </main>
    );
  }

  if (!state || !session) return <Spinner label="Loading the game" />;

  const match = state.match;
  const bracketOpen = bracketOverride ?? state.showBracket;

  return (
    <main className={`game-screen game-layout-${state.gameLayout} mx-auto w-full max-w-6xl px-4 py-6 transition-[max-width] lg:px-6`}>
      {/*
        The header is deliberately a strip above the player rather than an
        overlay on it: YouTube's terms forbid covering any part of an embedded
        player, so the operator text overlay planned for later needs a safe zone
        designed in from the start.
      */}
      <ReactionOverlay incoming={reaction} />
      <ChatOverlay incoming={chat} />
      <SuperReactionOverlay incoming={superReaction} durationMs={state.superReactionDurationMs} />

      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold tracking-tight">
            {match ? match.roundName : 'Song Battle'}
          </h1>
          <p className="flex flex-wrap items-center gap-x-2 text-sm text-text-muted">
            <span>
              {state.progress.played} of {state.progress.total} matches ·{' '}
              {state.amOperator ? 'you are running it' : `${state.operatorName} is running it`}
            </span>
            {/* The clock offset is what every progress bar in the room is
                computed from, so it stays visible during a game rather than
                only on the home screen — if playback ever looks wrong, this is
                the first number to look at. */}
            <span
              title={
                clock.synced
                  ? `Clock offset ${clock.offsetMs.toFixed(0)}ms, round trip ${clock.rttMs.toFixed(0)}ms`
                  : 'Still syncing with the server clock'
              }
              className={`font-mono text-xs ${clock.synced ? 'text-text-faint' : 'text-warning'}`}
            >
              {clock.synced ? `±${Math.abs(clock.offsetMs).toFixed(0)}ms` : 'syncing…'}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          {!state.amOperator && (
            <Button variant="ghost" onClick={() => void run(() => api.takeOver(lobbyId))}>
              Take over
            </Button>
          )}
          <Button variant="ghost" onClick={onBack}>
            Lobby
          </Button>
        </div>
      </header>

      {error && (
        <div className="mb-3">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      {state.audioMode === 'host_only' && match && (
        <div className="mb-3">
          <Banner>
            {state.amOperator
              ? 'You are playing for the room — make sure your Discord stream has audio on.'
              : `Audio comes through Discord from ${state.operatorName}.`}
          </Banner>
        </div>
      )}

      {match?.kind === 'revival' && (
        <div className="mb-3">
          <Banner tone="success">
            <strong>Wildcard revival.</strong> The best round-one loser gets another shot.
          </Banner>
        </div>
      )}

      {state.coinToss && (
        <div className="mb-3">
          <Banner tone="success">
            {state.coinToss.reason === 'mirror'
              ? 'Same entry on both sides — decided by coin toss.'
              : 'Tied, so it went to a coin toss.'}
          </Banner>
        </div>
      )}
      {state.coinToss && (() => {
        const winner = state.coinToss.winnerEntryId === state.match?.a?.id ? 'a' : 'b';
        const title = (winner === 'a' ? state.match?.a : state.match?.b)?.title;
        return <div className="coin-toss-overlay pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
          {/* Keyed on the match, so a second toss in the same session replays
              the animation from the top instead of showing a landed coin. */}
          <div key={`${state.match?.id ?? 'toss'}:${state.coinToss.winnerEntryId}`} className="coin-toss-stage">
            <div className={`coin-toss coin-toss-${winner}`} role="img" aria-label={`The coin landed on ${winner.toUpperCase()}`}>
              <div className="coin-spin">
                <div className="coin-face coin-face-a">A</div>
                <div className="coin-face coin-face-b">B</div>
              </div>
            </div>
            <p className="coin-toss-caption">
              {winner.toUpperCase()} wins the toss{title ? ` — ${title}` : ''}
            </p>
          </div>
        </div>;
      })()}

      <OverlayNotes
        notes={notes}
        canEdit={state.amOperator}
        onMove={(noteId, patch) => {
          // Optimistic: a drag that waited for the server would feel like mud.
          setNotes((current) =>
            current.map((n) => (n.id === noteId ? { ...n, ...patch } : n)),
          );
          void api.moveOverlay(lobbyId, noteId, patch).catch(() => void refresh());
        }}
        onRemove={(noteId) => {
          void api.removeOverlay(lobbyId, noteId).then(({ notes: next }) => setNotes(next));
        }}
      />

      {state.status === 'finished' ? (
        <ChampionScreen
          state={state}
          busy={busy}
          onPlayAgain={() =>
            void run(async () => {
              await api.resetLobby(lobbyId);
              onBack();
            })
          }
        />
      ) : match ? (
        <div className="flex flex-col gap-4">
          <Card className="match-card relative">
            <MatchStage
              match={match}
              serverNow={clock.serverNow}
              isOperator={match.canOperate}
              hostOnly={state.audioMode === 'host_only'}
              layout={state.gameLayout}
              busy={busy}
              onPlay={(slot) => void run(() => api.play(lobbyId, slot))}
              onResume={() => void run(() => api.resume(lobbyId))}
              onSkip={() => void run(() => api.skip(lobbyId))}
              onOpenVoting={() => void run(() => api.openVoting(lobbyId))}
              // Deliberately not through `run`: this fires from inside the
              // player, not from a click, so it must not flip the busy flag and
              // grey out the controls under the operator's hand.
              onStarted={(positionMs) => {
                void api.playbackStarted(lobbyId, positionMs).catch(() => {});
              }}
              // The operator's player is the clock in host-only mode, so this
              // is what the rest of the room's progress bar is drawn from.
              // Also not through `run`: it fires on a timer, not a click.
              onPosition={(positionMs, isPlaying) => {
                void api.reportPosition(lobbyId, positionMs, isPlaying).catch(() => {});
              }}
              onFallbackToYouTube={(entryId) =>
                void run(() => api.fallbackToYouTube(lobbyId, entryId))
              }
            />
          </Card>

          <VotePanel
            match={match}
            busy={busy}
            onVote={(entryId) => void run(() => api.vote(lobbyId, entryId))}
            onCloseVoting={() => void run(() => api.closeVoting(lobbyId))}
            onDecide={(entryId) => void run(() => api.decide(lobbyId, entryId))}
            onAdvance={() => void run(() => api.advance(lobbyId))}
            onVoteToAdvance={() => void run(() => api.voteToAdvance(lobbyId))}
            coinTossPending={state.coinTossPending}
            onCoinToss={() => void run(() => api.coinToss(lobbyId))}
            onGuess={(entryId, userId) => void run(() => api.guess(lobbyId, entryId, userId))}
          />

          {/* Hidden by default. Seeing who you would meet in the semis takes
              the surprise out of the night, and the champion screen shows the
              whole thing at the end anyway. */}
          <Card className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {state.reactionsEnabled && <ReactionBar
                onSend={(emoji) => socketRef.current?.emit('reaction:send', { lobbyId, emoji })}
              />}
              {state.chatMode === 'chat' && (
                <ChatComposer onSend={(text) => socketRef.current?.emit('chat:send', { lobbyId, text })} />
              )}
              {state.superReactionsEnabled && (
                <SuperReactionComposer onSend={(imageUrl, text) => socketRef.current?.emit('super-reaction:send', { lobbyId, imageUrl, text })} />
              )}
              {state.amOperator && (
                <Button variant="ghost" onClick={() => setShowOverlayTools((o) => !o)}>
                  {showOverlayTools ? 'Hide notes' : 'Put text on screen'}
                </Button>
              )}
            </div>

            {state.amOperator && showOverlayTools && (
              <OverlayComposer
                busy={busy}
                hasNotes={notes.length > 0}
                onAdd={(text) => {
                  void api.addOverlay(lobbyId, text).then(({ notes: next }) => setNotes(next));
                }}
                onClear={() => {
                  void api.removeOverlay(lobbyId, 'all').then(({ notes: next }) => setNotes(next));
                }}
              />
            )}
          </Card>

          {state.amHost && (
            <div className="flex flex-wrap justify-center gap-3 text-xs text-text-faint">
              <button className="hover:text-text" onClick={() => void run(() => api.setCommunication(lobbyId, { chatEnabled: state.chatMode !== 'chat' }))}>{state.chatMode === 'chat' ? 'Disable' : 'Enable'} chat messages</button>
              <button className="hover:text-text" onClick={() => void run(() => api.setCommunication(lobbyId, { reactionsEnabled: !state.reactionsEnabled }))}>{state.reactionsEnabled ? 'Disable' : 'Enable'} emojis</button>
              <button className="hover:text-text" onClick={() => void run(() => api.setCommunication(lobbyId, { superReactionsEnabled: !state.superReactionsEnabled }))}>{state.superReactionsEnabled ? 'Disable' : 'Enable'} super messages</button>
            </div>
          )}

          <Card>
            <button
              type="button"
              onClick={() => setBracketOverride(!bracketOpen)}
              aria-expanded={bracketOpen}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <h3 className="text-sm font-medium">Bracket</h3>
              <span className="text-xs text-text-faint">
                {bracketOpen ? 'hide' : 'peek'}
              </span>
            </button>
            {bracketOpen && (
              <div className="mt-2">
                <BracketView state={state} revealFuture={state.showBracket} />
              </div>
            )}
          </Card>
        </div>
      ) : (
        <Spinner label="Working out the next match" />
      )}
    </main>
  );
}
